import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { isValid } from 'date-fns';
import { FORM_FIELDS, PROFILE_PEOPLE_FIELDS_MAP, SESSION_STORAGE_FORM_KEY } from '../../../constants';
import { isDevelopment, omit, pick, removeUndefined } from '../../../helpers';
import { sessionStorage } from '../../../helpers/storage';
import { encodeDataToB64, decodeDataFromB64 } from '../../../helpers/form.new';
import { useFormData, useFormStore, useProfileDataUpdatedDate } from '../../../store/selectors/formStore';
import { extendFormData } from '../../../store/reducers/formStore';
import { useIsPage } from '../../../store/selectors/selection';
import { clearSessionStorageFormData, isNewData, updateModifiedData } from './helpers';

interface SessionStorageItem {
    data?: string;
    expInMs?: string | number;
    userId?: number | string;
    updated: string;
}

const SESSION_DATA_TTL_IN_MINUTES = 5;

const EXCLUDED_FIELDS_FOR_SESSION_STORAGE: Array<keyof FE.FormData> = [
    FORM_FIELDS.REGISTRATION_DADATA_FULL,
    FORM_FIELDS.RESIDENCE_DADATA_FULL,
    FORM_FIELDS.PLEDGE_DADATA_FULL,
    FORM_FIELDS.PHONE,
    FORM_FIELDS.AGREEMENT,
];

const FORM_FIELDS_FOR_SESSION_STORAGE = [
    ...Object.keys(omit(PROFILE_PEOPLE_FIELDS_MAP, EXCLUDED_FIELDS_FOR_SESSION_STORAGE)),
    FORM_FIELDS.RESIDENCE_SAME_REGISTRATION,
    FORM_FIELDS.PLEDGE_REALTY_ADDRESS_THE_SAME,
    FORM_FIELDS.AUTO_YEAR,
];

const isStorageItemNotExpired = (expirationTimeInMs = 0) => {
    return new Date().getTime() < expirationTimeInMs;
};

const getStorageItemExpirationTime = (): number => {
    return new Date().getTime() + SESSION_DATA_TTL_IN_MINUTES * 60 * 1000;
};

const saveFormDataToSessionStorage = (data: FE.FormData, userId?: string) => {
    const fields = removeUndefined(data, [''], true);
    const newItem: SessionStorageItem = {
        expInMs: getStorageItemExpirationTime(),
        data: JSON.stringify(fields),
        updated: new Date().toISOString(),
        ...(userId ? { userId } : {}),
    };
    sessionStorage.setItem(SESSION_STORAGE_FORM_KEY, encodeDataToB64(newItem));
};

const loadFormDataFromSessionStorage = (
    userId?: string,
): { data: FE.FormData; updated: Date; userId?: string } | null => {
    const raw = sessionStorage.getItem(SESSION_STORAGE_FORM_KEY);
    if (!raw) {
        return null;
    }

    try {
        const item = decodeDataFromB64<SessionStorageItem>(raw);
        if (!item) {
            clearSessionStorageFormData();
            return null;
        }
        const { expInMs, data, userId: savedId, updated } = item;
        const updatedDate = new Date(updated);
        if (
            !isValid(updatedDate) ||
            (userId && savedId && String(savedId) !== String(userId)) ||
            !expInMs ||
            !data ||
            !isStorageItemNotExpired(Number(expInMs))
        ) {
            clearSessionStorageFormData();
            return null;
        }

        return {
            data: removeUndefined(pick(JSON.parse(data), FORM_FIELDS_FOR_SESSION_STORAGE), ['']) as FE.FormData,
            updated: updatedDate,
            userId: savedId ? String(savedId) : undefined,
        };
    } catch (e) {
        console.error(e);
        return null;
    }
};

/** Сохраняет анкетные данные пользователя в Session Storage, так как сохранение данных в профиль происходит только при переходе на следующий шаг */
export const useSessionStorageData = (enabled = false) => {
    const { autoRun, userId: userIdFromStorage } = useFormStore();
    const isDataLoaded = useRef(false);
    const formData = useFormData();
    const formProfileData = useMemo(
        () => removeUndefined(pick(formData, FORM_FIELDS_FOR_SESSION_STORAGE), [''], true),
        [formData],
    );
    const prevFormDataRef = useRef<FE.FormData>(formProfileData);
    const profileUpdated = useProfileDataUpdatedDate();
    const dispatch = useDispatch();
    const { isWlPage } = useIsPage();

    // Загрузка данных из SessionStorage
    useEffect(() => {
        if (!enabled || isDataLoaded.current) {
            return;
        }
        isDataLoaded.current = true;

        if (isDevelopment) {
            console.groupCollapsed(`FormData from ${isWlPage ? 'WL SessionStorage' : 'ProfileService'} `);
            if (!isWlPage) {
                console.log('Updated at:', profileUpdated);
            }
            console.log(formProfileData);
            console.groupEnd();
        }

        if (!userIdFromStorage) {
            return;
        }

        const dataParsed = loadFormDataFromSessionStorage(userIdFromStorage);
        if (!dataParsed) {
            return;
        }

        const { data, updated, userId: savedId } = dataParsed;

        // Если данные из Profile Service актуальнее - очистить SessionStorage
        if (profileUpdated && profileUpdated.getTime() > updated.getTime()) {
            if (isDevelopment) {
                console.groupCollapsed('Cleared FormData from  SessionStorage');
                console.log({ profileUpdated, storageUpdated: updated });
                console.groupEnd();
            }
            clearSessionStorageFormData();
        } else {
            if (isDevelopment) {
                console.groupCollapsed('FormData from SessionStorage');
                console.log('Updated at:', updated);
                console.log('UserId:', savedId);
                console.log(data);
                console.groupEnd();
            }

            prevFormDataRef.current = {
                ...prevFormDataRef.current,
                ...data,
            };
            updateModifiedData(data);
            dispatch(extendFormData(data));
        }
    }, [enabled, dispatch, userIdFromStorage]);

    // Сохранение всех полей, измененных после загрузки из PS, но которые не были сохранены в PS
    useEffect(() => {
        if (
            !enabled ||
            autoRun ||
            !isDataLoaded.current ||
            !userIdFromStorage ||
            !isNewData(prevFormDataRef.current, formProfileData)
        ) {
            return;
        }
        const update = updateModifiedData(formProfileData);

        if (isDevelopment) {
            console.groupCollapsed('Save modified FormData to SessionStorage');
            console.log(update);
            console.groupEnd();
        }
        saveFormDataToSessionStorage(update, userIdFromStorage);
        prevFormDataRef.current = formProfileData;
    }, [autoRun, formProfileData, enabled, userIdFromStorage]);
};
